import { useEffect, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { useAuth } from "../../context/AuthContext";
import { useOffline } from "../../context/OfflineContext";
import { AlertTriangle, WifiOff, RefreshCw } from "lucide-react";
import { TourGuiado } from "../tour/TourGuiado";
import { obtenerEstadoVigencia } from "../../lib/vigenciaOffline";
import { api } from "../../api/client";

// Bloquea la app cuando lleva más de 15 días sin lograr conectarse con
// éxito al servidor (ver lib/vigenciaOffline.ts). Solo se aplica mientras
// NO hay conexión: si el negocio sí tiene internet, el propio /auth/me y las
// pantallas normales (solo lectura, suscripción vencida) ya se encargan de
// aplicar el estado real de la suscripción en tiempo real.
function usarBloqueoVigencia(enLinea: boolean) {
  const [expirada, setExpirada] = useState(false);

  useEffect(() => {
    function revisar() {
      const estado = obtenerEstadoVigencia();
      setExpirada(!estado.valido);
    }
    revisar();
    const intervalo = setInterval(revisar, 5 * 60 * 1000);
    return () => clearInterval(intervalo);
  }, []);

  return !enLinea && expirada;
}

function PantallaVigenciaVencida() {
  const [reintentando, setReintentando] = useState(false);

  async function reintentar() {
    setReintentando(true);
    try {
      await api.get("/auth/me");
      window.location.reload();
    } catch {
      setReintentando(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 text-center">
      <div className="max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <WifiOff size={32} className="mx-auto mb-3 text-amber-400" />
        <p className="font-semibold text-slate-700">Necesitas conectarte a internet</p>
        <p className="mt-2 text-sm text-slate-500">
          Llevas más de 15 días sin conexión. Para seguir usando el sistema sin internet, primero necesitamos
          validar en línea que tu suscripción sigue vigente.
        </p>
        <button
          onClick={reintentar}
          disabled={reintentando}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm disabled:opacity-60"
        >
          <RefreshCw size={15} className={reintentando ? "animate-spin" : ""} />
          {reintentando ? "Conectando..." : "Reintentar conexión"}
        </button>
      </div>
    </div>
  );
}

export function AppLayout() {
  const { soloLectura, suscripcionVencida, mostrarTour } = useAuth();
  const { enLinea } = useOffline();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const vigenciaVencida = usarBloqueoVigencia(enLinea);
  const ubicacion = useLocation();

  if (vigenciaVencida) {
    return <PantallaVigenciaVencida />;
  }

  return (
    <div className="fondo-marca-agua flex min-h-screen">
      <Sidebar abierto={menuAbierto} onCerrar={() => setMenuAbierto(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar onAbrirMenu={() => setMenuAbierto(true)} />
        {soloLectura && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-sm px-6 py-2.5 flex items-center gap-2">
            <AlertTriangle size={16} />
            Este negocio está en modo de solo lectura. Puedes consultar la información, pero no realizar cambios. Contacta a Mascotas Vet para reactivar tu cuenta.
          </div>
        )}
        {/* Aviso de suscripción vencida — no se repite si ya se está mostrando
            el de "solo lectura" de arriba (ambos pueden coincidir cuando el
            negocio ya llegó a la suspensión por falta de pago). */}
        {suscripcionVencida && !soloLectura && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-sm px-6 py-2.5 flex items-center gap-2">
            <AlertTriangle size={16} />
            Tu suscripción está vencida.{" "}
            <Link to="/suscripcion" className="font-medium underline hover:text-amber-900">
              Ver detalles y regularizar
            </Link>
          </div>
        )}
        <main className="flex-1 p-6">
          <div key={ubicacion.pathname} className="entrada-contenido">
            <Outlet />
          </div>
        </main>
      </div>
      {mostrarTour && <TourGuiado />}
    </div>
  );
}
